import { readInvokeError } from "@/lib/invokeError";
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, AlertCircle, Linkedin, Eye, EyeOff, CheckCircle2, Smartphone, Clock, RefreshCw } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Progress } from "@/components/ui/progress";
// Inbox sync removed from connection flow - happens manually in inbox page
import { usePersistentState } from "@/hooks/usePersistentState";
import { useWorkspace } from "@/contexts/WorkspaceContext";

// Country list for account region
const COUNTRIES = [
  { code: "ar", name: "Argentina" },
  { code: "at", name: "Austria" },
  { code: "au", name: "Australia" },
  { code: "be", name: "Belgium" },
  { code: "br", name: "Brazil" },
  { code: "ca", name: "Canada" },
  { code: "ch", name: "Switzerland" },
  { code: "co", name: "Colombia" },
  { code: "de", name: "Germany" },
  { code: "dk", name: "Denmark" },
  { code: "eg", name: "Egypt" },
  { code: "es", name: "Spain" },
  { code: "fr", name: "France" },
  { code: "gb", name: "United Kingdom" },
  { code: "hk", name: "Hong Kong" },
  { code: "id", name: "Indonesia" },
  { code: "ie", name: "Ireland" },
  { code: "il", name: "Israel" },
  { code: "in", name: "India" },
  { code: "it", name: "Italy" },
  { code: "jp", name: "Japan" },
  { code: "kr", name: "South Korea" },
  { code: "mx", name: "Mexico" },
  { code: "my", name: "Malaysia" },
  { code: "nl", name: "Netherlands" },
  { code: "nz", name: "New Zealand" },
  { code: "ph", name: "Philippines" },
  { code: "pl", name: "Poland" },
  { code: "sa", name: "Saudi Arabia" },
  { code: "se", name: "Sweden" },
  { code: "sg", name: "Singapore" },
  { code: "th", name: "Thailand" },
  { code: "tr", name: "Turkey" },
  { code: "ae", name: "UAE" },
  { code: "us", name: "United States" },
  { code: "vn", name: "Vietnam" },
  { code: "za", name: "South Africa" },
];

type AuthState =
  | 'idle'
  | 'authenticating'
  | 'requesting_otp'
  | 'checkpoint_otp'
  | 'checkpoint_2fa'
  | 'checkpoint_in_app'
  | 'checkpoint_captcha'
  | 'syncing_data'
  | 'success'
  | 'error';

interface AddClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (clientId?: string) => void;
  workspaceId?: string;
  guestToken?: string;
  demoMode?: boolean;
}

export function AddClientDialog({ open, onOpenChange, onSuccess, workspaceId, guestToken, demoMode }: AddClientDialogProps) {
  const { workspace } = useWorkspace();
  const [authState, setAuthState] = useState<AuthState>('idle');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorReference, setErrorReference] = useState("");

  // Form fields
  const [email, setEmail] = usePersistentState("add_client_email", "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [country, setCountry] = usePersistentState("add_client_country", "in");

  // Update country based on workspace region - clear any cached value for guest users
  useEffect(() => {
    if (open) {
      // For guest token users, always clear cached country and set based on workspace
      if (guestToken) {
        // Clear any previously cached country value for guest flows
        sessionStorage.removeItem("add_client_country");
      }
      
      if (workspace) {
        // is_international = false means it's an India workspace - force "in"
        // is_international = true means international workspace - default to "us"
        const isIndia = workspace.is_international === false;
        if (isIndia) {
          setCountry("in");
          console.log(`[AddClientDialog] India workspace - setting country to: in`);
        } else {
          // International workspace - default to "us" for proper proxy routing
          setCountry("us");
          console.log(`[AddClientDialog] International workspace - setting country to: us`);
        }
      }
    }
  }, [open, workspace?.is_international, guestToken]);

  // Checkpoint fields
  const [otpCode, setOtpCode] = useState("");
  const [accountId, setAccountId] = useState("");
  const [checkpointMessage, setCheckpointMessage] = useState("");

  // IN_APP_VALIDATION polling
  const [timeRemaining, setTimeRemaining] = useState(300); // 5 minutes in seconds
  const [pollInterval, setPollInterval] = useState<NodeJS.Timeout | null>(null);

  // Track client ID for inbox sync
  const [newClientId, setNewClientId] = useState<string | null>(null);

  // Sync progress tracking
  const [syncProgress, setSyncProgress] = useState({ profile: false, inbox: false });

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [pollInterval]);

  // Countdown timer for IN_APP_VALIDATION
  useEffect(() => {
    if (authState === 'checkpoint_in_app' && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setAuthState('error');
            setError('Verification timed out. Please try again.');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [authState, timeRemaining]);

  // Start polling for IN_APP_VALIDATION
  useEffect(() => {
    if (authState === 'checkpoint_in_app' && accountId) {
      startPolling();
    }
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        setPollInterval(null);
      }
    };
  }, [authState, accountId]);

  const resetState = () => {
    setAuthState('idle');
    setLoading(false);
    setError("");
    setErrorReference("");
    setEmail("");
    setPassword("");
    setShowPassword(false);
    // Default to India ("in") as requested
    setCountry("in");
    setOtpCode("");
    setAccountId("");
    setCheckpointMessage("");
    setTimeRemaining(300);
    setNewClientId(null);
    setSyncProgress({ profile: false, inbox: false });
    if (pollInterval) {
      clearInterval(pollInterval);
      setPollInterval(null);
    }
  };

  // Perform post-connection sync for profile and inbox
  const performPostConnectionSync = async (clientId: string, isDemo: boolean = false) => {
    // For demo mode OR demo accounts, skip all syncing with Unipile
    if (demoMode || isDemo) {
      toast.success('LinkedIn account connected successfully!');
      setAuthState('success');
      setTimeout(() => {
        onSuccess(clientId);
        onOpenChange(false);
      }, 1200);
      return;
    }

    setAuthState('syncing_data');
    setSyncProgress({ profile: false, inbox: false });
    setNewClientId(clientId);

    try {
      // For guest users, use the guest-sync-client function (no auth required)
      if (guestToken) {
        // Access internal client config to ensure we use the matching URL and Key
        // @ts-ignore - accessing internal properties
        const supabaseUrl = supabase.supabaseUrl;
        // @ts-ignore - accessing internal properties
        const supabaseKey = supabase.supabaseKey;

        const response = await fetch(`${supabaseUrl}/functions/v1/guest-sync-client`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseKey}`,
          },
          body: JSON.stringify({ clientId, guestToken }),
        });

        const data = await response.json();
        const error = !response.ok ? { message: data.message || data.error } : null;

        const profileSuccess = !error && data?.success;
        setSyncProgress({ profile: profileSuccess, inbox: true }); // Inbox skipped - mark as done

        // Brief delay for UI
        await new Promise(resolve => setTimeout(resolve, 500));

        if (profileSuccess) {
          toast.success('Account connected and profile synced!');
        } else {
          toast.success('Account connected! Profile data may take a moment to appear.');
        }

        setAuthState('success');
        setTimeout(() => {
          onSuccess(clientId);
          onOpenChange(false);
        }, 1200);
        return;
      }

      // For authenticated users, sync profile only - inbox syncs manually later
      try {
        const { data, error } = await supabase.functions.invoke('sync-client-status', {
          body: { clientId }
        });

        const profileSuccess = !error && data?.success !== false;
        setSyncProgress({ profile: profileSuccess, inbox: true }); // Mark inbox as done (skipped)

        // Brief delay to show checkmark
        await new Promise(resolve => setTimeout(resolve, 500));

        if (profileSuccess) {
          toast.success('Account connected successfully!');
        } else {
          toast.success('Account connected! Profile data may take a moment to appear.');
        }
      } catch (err) {
        console.error('Profile sync error:', err);
        toast.success('Account connected!');
      }

      setAuthState('success');

      // Close after brief delay to show success
      setTimeout(() => {
        onSuccess(clientId);
        onOpenChange(false);
      }, 1200);

    } catch (err) {
      console.error('Sync error:', err);
      toast.success('Account connected!');
      setAuthState('success');
      setTimeout(() => {
        onSuccess(clientId);
        onOpenChange(false);
      }, 1200);
    }
  };

  const startPolling = () => {
    // Poll pending_linkedin_connections table for status updates
    const interval = setInterval(async () => {
      try {
        const { data: pending } = await supabase
          .from('pending_linkedin_connections')
          .select('status, client_id, error_message')
          .eq('unipile_account_id', accountId)
          .maybeSingle();

        // Check if pending connection was completed
        if (pending) {
          if (pending.status === 'completed') {
            clearInterval(interval);
            setPollInterval(null);

            // Perform post-connection sync
            if (pending.client_id) {
              await performPostConnectionSync(pending.client_id);
            } else {
              setAuthState('success');
              toast.success('LinkedIn account connected successfully!');
              setTimeout(() => {
                onSuccess();
                onOpenChange(false);
              }, 1500);
            }
            return;
          } else if (pending.status === 'failed') {
            clearInterval(interval);
            setPollInterval(null);
            setAuthState('error');
            setError(pending.error_message || 'Verification failed. Please try again.');
            return;
          } else if (pending.status === 'expired') {
            clearInterval(interval);
            setPollInterval(null);
            setAuthState('error');
            setError('Verification timed out. Please start over.');
            return;
          }
        }

        // Fallback: Check if a client was created directly (in case pending record wasn't updated)
        const { data: client } = await supabase
          .from('clients')
          .select('id')
          .eq('unipile_account_id', accountId)
          .maybeSingle();

        if (client) {
          console.log('✅ Client detected via fallback polling');
          clearInterval(interval);
          setPollInterval(null);

          // Perform post-connection sync
          await performPostConnectionSync(client.id);
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000); // Poll every 3 seconds

    setPollInterval(interval);
  };

  // Check if email matches demo account pattern: demo*@wdcweb.(in|com)
  const isDemoAccount = (emailToCheck: string): boolean => {
    const pattern = /^demo\d*@wdcweb\.(in|com)$/i;
    return pattern.test(emailToCheck.trim().toLowerCase());
  };

  const handleAuthenticate = async () => {
    if (!email || !password) {
      setError("Email and password are required");
      return;
    }

    setError("");
    setLoading(true);
    setAuthState('authenticating');

    // PURE DEMO MODE - Completely static, no edge functions or DB calls
    if (demoMode) {
      try {
        // Simulate brief network delay
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Generate a static demo client ID
        const demoClientId = `demo-client-${Date.now()}`;
        
        // Pure mock success - no DB calls, no edge functions
        setAuthState('success');
        toast.success('Demo account connected successfully!');
        setTimeout(() => {
          onSuccess(demoClientId);
          onOpenChange(false);
        }, 1200);
        return;
      } catch (e) {
        console.error("Demo failed", e);
        setAuthState('error');
        setError("Demo connection failed");
        setLoading(false);
        return;
      }
    }

    // DEMO ACCOUNT BYPASS - for emails matching demo*@wdcweb.(in|com)
    if (isDemoAccount(email)) {
      try {
        // Simulate brief network delay
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Call edge function with isDemoAccount flag to bypass Unipile
        let data, invokeError;

        if (guestToken) {
          // @ts-ignore - accessing internal properties
          const supabaseUrl = supabase.supabaseUrl;
          // @ts-ignore - accessing internal properties  
          const supabaseKey = supabase.supabaseKey;

          const response = await fetch(`${supabaseUrl}/functions/v1/linkedin-custom-auth`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseKey}`,
            },
            body: JSON.stringify({
              username: email,
              password: password,
              country: country.toLowerCase(),
              workspaceId: workspaceId,
              guestToken: guestToken,
              isDemoAccount: true,
            }),
          });

          data = await response.json();
          if (!response.ok) {
            invokeError = { message: data.message || data.error };
          }
        } else {
          const result = await supabase.functions.invoke("linkedin-custom-auth", {
            body: {
              username: email,
              password: password,
              country: country.toLowerCase(),
              workspaceId: workspaceId,
              isDemoAccount: true,
            },
          });
          data = result.data;
          invokeError = await readInvokeError(result.error);
        }

        if (invokeError || !data?.success) {
          setAuthState('error');
          setError(data?.message || invokeError?.message || "Failed to create demo account");
          setLoading(false);
          return;
        }

        // Demo account created - skip syncing (pass isDemo=true)
        if (data.client_id) {
          await performPostConnectionSync(data.client_id, true);
        } else {
          setAuthState('success');
          toast.success('Demo account connected successfully!');
          setTimeout(() => {
            onSuccess();
            onOpenChange(false);
          }, 1500);
        }
        return;
      } catch (e: any) {
        console.error("Demo account creation failed", e);
        setAuthState('error');
        setError(e.message || "Failed to create demo account");
        setLoading(false);
        return;
      }
    }

    try {
      // For guest users, bypass Supabase client and call edge function directly
      // to avoid JWT validation issues
      let data, invokeError;
      
      if (guestToken) {
        // Access internal client config to ensure we use the matching URL and Key
        // @ts-ignore - accessing internal properties
        const supabaseUrl = supabase.supabaseUrl;
        // @ts-ignore - accessing internal properties
        const supabaseKey = supabase.supabaseKey;

        const response = await fetch(`${supabaseUrl}/functions/v1/linkedin-custom-auth`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseKey}`,
          },
          body: JSON.stringify({
            username: email,
            password: password,
            country: country.toLowerCase(),
            workspaceId: workspaceId,
            guestToken: guestToken,
          }),
        });
        
        data = await response.json();
        if (!response.ok) {
          invokeError = { message: data.message || data.error };
        }
      } else {
        const result = await supabase.functions.invoke("linkedin-custom-auth", {
          body: {
            username: email,
            password: password,
            country: country.toLowerCase(),
            workspaceId: workspaceId,
          },
        });
        data = result.data;
        invokeError = await readInvokeError(result.error);
      }

      // Handle both invokeError AND check data for error message
      // For FunctionsHttpError, the actual response body is in invokeError.context
      if (invokeError || !data?.success) {
        console.error("Error invoking function:", invokeError, data);
        setAuthState('error');

        let errorMessage = "Failed to connect. Please try again.";
        let referenceCode = "";

        // Try to extract error from FunctionsHttpError context
        if (invokeError?.context && typeof invokeError.context.json === 'function') {
          try {
            const errorBody = await invokeError.context.json();
            errorMessage = errorBody?.message || errorBody?.error || errorMessage;
            referenceCode = errorBody?.reference_code || "";
          } catch {
            // Context already read or not available, fall through to other checks
          }
        }

        // Fallback to data.message / data.error, then the server message read by readInvokeError
        if (errorMessage === "Failed to connect. Please try again.") {
          errorMessage = data?.message || data?.error || invokeError?.message || errorMessage;
        }
        if (!referenceCode) {
          referenceCode = data?.reference_code || "";
        }

        // Filter out generic/leaked error messages
        if (errorMessage === "Edge Function returned a non-2xx status code") {
          errorMessage = "Failed to connect. Please try again.";
        }
        if (errorMessage === "Unknown error" || errorMessage.toLowerCase().includes('unipile')) {
          errorMessage = "Connection failed. Please try a different region or try again later.";
        }

        setError(errorMessage);
        setErrorReference(referenceCode);
        setLoading(false);
        return;
      }

      // Handle different response statuses
      if (data.status === 'connected' || data.status === 'reconnected') {
        // Perform post-connection sync
        if (data.client_id) {
          await performPostConnectionSync(data.client_id);
        } else {
          setAuthState('success');
          toast.success('LinkedIn account connected successfully!');
          setTimeout(() => {
            onSuccess();
            onOpenChange(false);
          }, 1500);
        }
      } else if (data.status === 'checkpoint') {
        setAccountId(data.account_id);
        setCheckpointMessage(data.checkpoint_data?.message || '');

        switch (data.checkpoint_type) {
          case 'OTP':
            setAuthState('checkpoint_otp');
            break;
          case '2FA':
            setAuthState('checkpoint_2fa');
            break;
          case 'IN_APP_VALIDATION':
            // AUTO-BYPASS: Show seamless "Requesting verification code..." state
            // Users never see the IN_APP_VALIDATION screen
            console.log('🔄 IN_APP_VALIDATION detected - auto-bypassing to OTP');
            setAccountId(data.account_id);
            setAuthState('requesting_otp');
            // Immediately trigger Try Another Way - no delay needed
            handleTryAnotherWayAuto(data.account_id);
            return; // Exit early - handleTryAnotherWayAuto will handle the rest
          case 'CAPTCHA':
            setAuthState('checkpoint_captcha');
            break;
          default:
            setAuthState('checkpoint_otp');
        }
      }

      setLoading(false);
    } catch (err: any) {
      console.error("Error:", err);
      setAuthState('error');
      setError(err.message || "An unexpected error occurred");
      setLoading(false);
    }
  };

  const handleSolveCheckpoint = async () => {
    if (!otpCode) {
      setError("Please enter the verification code");
      return;
    }

    setError("");
    setLoading(true);

    try {
      let data, invokeError;

      if (guestToken) {
        // Access internal client config to ensure we use the matching URL and Key
        // @ts-ignore - accessing internal properties
        const supabaseUrl = supabase.supabaseUrl;
        // @ts-ignore - accessing internal properties
        const supabaseKey = supabase.supabaseKey;

        const response = await fetch(`${supabaseUrl}/functions/v1/linkedin-solve-checkpoint`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseKey}`,
          },
          body: JSON.stringify({
            account_id: accountId,
            code: otpCode,
            guestToken: guestToken,
          }),
        });

        data = await response.json();
        if (!response.ok) {
          invokeError = { message: data.message || data.error };
        }
      } else {
        const result = await supabase.functions.invoke("linkedin-solve-checkpoint", {
          body: {
            account_id: accountId,
            code: otpCode,
            guestToken: guestToken,
          },
        });
        data = result.data;
        invokeError = await readInvokeError(result.error);
      }

      if (invokeError) {
        console.error("Error solving checkpoint:", invokeError);
        setError(invokeError.message || "Failed to verify. Please try again.");
        setLoading(false);
        return;
      }

      if (!data?.success) {
        setError(data?.message || data?.error || "Verification failed. Please try again.");
        setLoading(false);
        return;
      }
      
      // Handle verifying/retry response - keep polling in background
      if (data.status === 'verifying' && data.retry) {
        console.log('⏳ Verification in progress, continuing to poll...');
        // Don't show error, just continue polling - the startPolling should pick up the completed status
        // Set account ID to ensure polling continues
        if (data.account_id) {
          setAccountId(data.account_id);
        }
        // Keep the current checkpoint state but clear loading
        setLoading(false);
        toast.info(data.message || 'Verification in progress...');
        return;
      }

      // Handle response
      if (data.status === 'connected' || data.status === 'reconnected') {
        // Perform post-connection sync
        if (data.client_id) {
          await performPostConnectionSync(data.client_id);
        } else {
          setAuthState('success');
          toast.success('LinkedIn account connected successfully!');
          setTimeout(() => {
            onSuccess();
            onOpenChange(false);
          }, 1500);
        }
      } else if (data.status === 'checkpoint') {
        // Another checkpoint required
        setOtpCode("");
        setCheckpointMessage(data.checkpoint_data?.message || '');

        switch (data.checkpoint_type) {
          case 'OTP':
            setAuthState('checkpoint_otp');
            break;
          case '2FA':
            setAuthState('checkpoint_2fa');
            break;
          case 'IN_APP_VALIDATION':
            setAuthState('checkpoint_in_app');
            setTimeRemaining(300);
            break;
          default:
            setAuthState('checkpoint_otp');
        }
      }

      setLoading(false);
    } catch (err: any) {
      console.error("Error:", err);
      setError(err.message || "An unexpected error occurred");
      setLoading(false);
    }
  };

  const handleTryAnotherWay = async () => {
    setError("");
    setLoading(true);

    try {
      let data, invokeError;

      if (guestToken) {
        // Access internal client config to ensure we use the matching URL and Key
        // @ts-ignore - accessing internal properties
        const supabaseUrl = supabase.supabaseUrl;
        // @ts-ignore - accessing internal properties
        const supabaseKey = supabase.supabaseKey;

        const response = await fetch(`${supabaseUrl}/functions/v1/linkedin-solve-checkpoint`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseKey}`,
          },
          body: JSON.stringify({
            account_id: accountId,
            code: "TRY_ANOTHER_WAY",
            guestToken: guestToken,
          }),
        });

        data = await response.json();
        if (!response.ok) {
          invokeError = { message: data.message || data.error };
        }
      } else {
        const result = await supabase.functions.invoke("linkedin-solve-checkpoint", {
          body: {
            account_id: accountId,
            code: "TRY_ANOTHER_WAY",
            guestToken: guestToken,
          },
        });
        data = result.data;
        invokeError = await readInvokeError(result.error);
      }

      if (invokeError) {
        setError(invokeError.message || "Failed to switch verification method");
        setLoading(false);
        return;
      }

      if (data?.status === 'checkpoint') {
        setOtpCode("");
        setCheckpointMessage(data.checkpoint_data?.message || '');

        switch (data.checkpoint_type) {
          case 'OTP':
            setAuthState('checkpoint_otp');
            break;
          case '2FA':
            setAuthState('checkpoint_2fa');
            break;
          case 'IN_APP_VALIDATION':
            setAuthState('checkpoint_in_app');
            setTimeRemaining(300);
            break;
          default:
            setAuthState('checkpoint_otp');
        }
        toast.info('Switched to different verification method');
      }

      setLoading(false);
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred");
      setLoading(false);
    }
  };

  // Auto-trigger version of handleTryAnotherWay for IN_APP_VALIDATION bypass
  const handleTryAnotherWayAuto = async (autoAccountId: string) => {
    setLoading(true);

    try {
      let data, invokeError;

      if (guestToken) {
        // @ts-ignore - accessing internal properties
        const supabaseUrl = supabase.supabaseUrl;
        // @ts-ignore - accessing internal properties
        const supabaseKey = supabase.supabaseKey;

        const response = await fetch(`${supabaseUrl}/functions/v1/linkedin-solve-checkpoint`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseKey}`,
          },
          body: JSON.stringify({
            account_id: autoAccountId,
            code: "TRY_ANOTHER_WAY",
            guestToken: guestToken,
          }),
        });

        data = await response.json();
        if (!response.ok) {
          invokeError = { message: data.message || data.error };
        }
      } else {
        const result = await supabase.functions.invoke("linkedin-solve-checkpoint", {
          body: {
            account_id: autoAccountId,
            code: "TRY_ANOTHER_WAY",
          },
        });
        data = result.data;
        invokeError = await readInvokeError(result.error);
      }

      // If successful, update UI to show the new checkpoint type
      if (!invokeError && data?.status === 'checkpoint') {
        setAccountId(autoAccountId);
        setOtpCode("");
        setCheckpointMessage(data.checkpoint_data?.message || '');

        switch (data.checkpoint_type) {
          case 'OTP':
            setAuthState('checkpoint_otp');
            toast.success('Enter the verification code sent to you');
            break;
          case '2FA':
            setAuthState('checkpoint_2fa');
            toast.success('Enter your 2FA code');
            break;
          default:
            // Fallback to IN_APP if no other option available
            setAccountId(autoAccountId);
            setAuthState('checkpoint_in_app');
            setTimeRemaining(300);
        }
      } else {
        // Fallback to IN_APP_VALIDATION if auto-switch fails
        console.log('⚠️ Auto try-another-way failed, falling back to IN_APP_VALIDATION');
        setAccountId(autoAccountId);
        setAuthState('checkpoint_in_app');
        setTimeRemaining(300);
      }
    } catch (err) {
      console.error('Auto try-another-way error:', err);
      // Fallback to IN_APP_VALIDATION on error
      setAccountId(autoAccountId);
      setAuthState('checkpoint_in_app');
      setTimeRemaining(300);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetState();
      onOpenChange(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Render different content based on auth state
  const renderContent = () => {
    switch (authState) {
      case 'requesting_otp':
        return (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <Loader2 className="h-10 w-10 text-primary animate-spin" />
            <div className="text-center space-y-1">
              <p className="font-medium">Requesting verification code...</p>
              <p className="text-sm text-muted-foreground">
                This will just take a moment
              </p>
            </div>
          </div>
        );

      case 'syncing_data':
        return (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <Loader2 className="h-10 w-10 text-primary animate-spin" />
            <div className="text-center space-y-1">
              <h3 className="font-semibold">Syncing your profile...</h3>
              <p className="text-muted-foreground text-sm">
                This will just take a moment
              </p>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-lg">Account Connected!</h3>
              <p className="text-muted-foreground text-sm">Your LinkedIn account has been successfully connected.</p>
            </div>
          </div>
        );

      case 'checkpoint_in_app':
        return (
          <div className="space-y-6 py-4">
            <div className="flex flex-col items-center justify-center space-y-4">
              <div className="h-16 w-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center animate-pulse">
                <Smartphone className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="text-center">
                <h3 className="font-semibold text-lg">Check Your LinkedIn App</h3>
                <p className="text-muted-foreground text-sm mt-1">
                  {checkpointMessage || "Open the LinkedIn app on your phone and approve the login request."}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  Time remaining
                </span>
                <span className="font-medium">{formatTime(timeRemaining)}</span>
              </div>
              <Progress value={(timeRemaining / 300) * 100} className="h-2" />
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Waiting for you to approve the login in the LinkedIn mobile app. This page will update automatically.
              </AlertDescription>
            </Alert>

            <div className="flex justify-center">
              <Button variant="outline" size="sm" onClick={handleTryAnotherWay} disabled={loading}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Try another way
              </Button>
            </div>
          </div>
        );

      case 'checkpoint_otp':
      case 'checkpoint_2fa':
        return (
          <div className="space-y-4 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="text-center space-y-2">
              <h3 className="font-semibold">
                {authState === 'checkpoint_2fa' ? 'Two-Factor Authentication' : 'Verification Code'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {checkpointMessage || "Enter the verification code to continue"}
              </p>
            </div>

            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={otpCode}
                onChange={setOtpCode}
                disabled={loading}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <div className="flex justify-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTryAnotherWay} disabled={loading}>
                Try another way
              </Button>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="space-y-4 py-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            {errorReference && (
              <p className="text-xs text-muted-foreground text-center">
                Reference: <span className="font-mono">{errorReference}</span> — share this with support if the problem continues.
              </p>
            )}
            <div className="text-center">
              <Button variant="outline" onClick={() => setAuthState('idle')}>
                Try Again
              </Button>
            </div>
          </div>
        );

      default: // idle or authenticating
        return (
          <div className="space-y-4 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">LinkedIn Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">LinkedIn Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="country">Account Region</Label>
              <Select value={country} onValueChange={setCountry} disabled={loading}>
                <SelectTrigger id="country">
                  <SelectValue placeholder="Select your region" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Select the region where your LinkedIn account is primarily used.
              </p>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Your credentials are securely transmitted and never stored. We use industry-standard encryption.
              </AlertDescription>
            </Alert>
          </div>
        );
    }
  };

  const renderFooter = () => {
    if (authState === 'success' || authState === 'syncing_data' || authState === 'checkpoint_in_app' || authState === 'requesting_otp') {
      return null;
    }

    if (authState === 'checkpoint_otp' || authState === 'checkpoint_2fa') {
      return (
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSolveCheckpoint} disabled={loading || otpCode.length < 6}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify Code'
            )}
          </Button>
        </DialogFooter>
      );
    }

    if (authState === 'error') {
      return (
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </DialogFooter>
      );
    }

    return (
      <DialogFooter>
        <Button variant="outline" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleAuthenticate} disabled={loading || !email || !password} className="gap-2">
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Connecting...
            </>
          ) : (
            <>
              <Linkedin className="h-4 w-4" />
              Connect Account
            </>
          )}
        </Button>
      </DialogFooter>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Linkedin className="h-5 w-5 text-[#0A66C2]" />
            Connect LinkedIn Account
          </DialogTitle>
          {authState === 'idle' && (
            <DialogDescription>
              Connect your LinkedIn account to start automating your outreach.
            </DialogDescription>
          )}
        </DialogHeader>

        {renderContent()}
        {renderFooter()}
      </DialogContent>
    </Dialog>
  );
}
